import {
  TellescopeModelFromName,
  useAuth,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
  useUsersMap,
} from "@tellescope/tanstack-query";
import { Badge, Button, type VirtualizedColumnDef, useDebouncedValue, VirtualizedDataTableProps } from "@tellescope/ui";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useTableView, TELLESCOPE_2_TABLE_VIEW_PREFIX } from "./useTableView";
import { useTableViewConfig } from "./useTableViewConfig";
import { DEFAULT_CONTACTS_VIEW, useShallowContactsDefaultViewStore } from "../store";
import { type ContactsFilters, countContactActiveFilters } from "../components/contacts/contactFilters";
import { buildMdbFilterFromFreeform } from "../components/tables/freeformFilters";
import { formatDate, formatName, formatPhone } from "@tellescope/utils";
import { ALL_ENDUSER_FIELDS_TO_DISPLAY_NAME } from "@tellescope/constants";
import { EnduserFieldCell } from "../components/tables";
import { useTranslation } from "react-i18next";

const ENDUSER_FIELD_KEYS_ALREADY_COVERED = new Set(["fname", "email", "phone", "assignedTo", "tags", "dateOfBirth"]);

type EnduserRecord = TellescopeModelFromName<"endusers">;
type EnduserColumnDef = VirtualizedColumnDef<EnduserRecord>;

type PartialEnduserColumnDef<Value = unknown> = Partial<EnduserColumnDef> & {
  label?: string;
  cell?: EnduserColumnDef["cell"];
} & ({ accessorKey?: keyof EnduserRecord } | { accessorFn: (originalRow: EnduserRecord, index: number) => Value });

type ContactsColumnFieldName = (typeof DEFAULT_CONTACTS_VIEW)["columns"][number]["field"];

export interface UseEnduserTableArgs {
  viewId?: string;
  setViewId?: Dispatch<SetStateAction<string>>;
  onSelectEnduser: (enduserId: string) => void;
}

export const useEnduserTable = (
  { viewId: providedViewId, setViewId, onSelectEnduser }: UseEnduserTableArgs = { onSelectEnduser: () => {} }
) => {
  const { t } = useTranslation("contacts");
  const { selectedOrganization: organization, currentUser } = useAuth();
  const userId = currentUser.id;
  const userRecordsById = useUsersMap();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery] = useDebouncedValue(searchQuery);
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);

  const { defaultViewByUserId, setUserDefaultView } = useShallowContactsDefaultViewStore(
    "defaultViewByUserId",
    "setUserDefaultView"
  );

  const fieldToColumnConfigMap = useMemo<Record<ContactsColumnFieldName | string, PartialEnduserColumnDef>>(
    () => ({
      Name: {
        accessorKey: "fname",
        label: t("Name"),
        cell: ({ row }) => {
          const enduser = row.original;
          return (
            <Button
              variant="link"
              className="truncate w-full justify-start"
              data-trackingid="contacts-page-open-contact-dialog"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onSelectEnduser(enduser.id);
              }}
            >
              {enduser.displayName || formatName(enduser) || t("User {{id}}", { id: enduser.id })}
            </Button>
          );
        },
      },
      Email: {
        accessorKey: "email",
        label: t("Email"),
        cell: ({ row }) => {
          const email = row.original.email;
          return (
            <div className="text-sm truncate" title={email || undefined}>
              {email || <span className="text-muted-foreground">{t("No email")}</span>}
            </div>
          );
        },
      },
      Phone: {
        accessorKey: "phone",
        label: t("Phone"),
        cell: ({ row }) => {
          const phone = row.original.phone;
          return (
            <div className="text-sm truncate" title={phone || undefined}>
              {formatPhone(phone) || <span className="text-muted-foreground">{t("No phone")}</span>}
            </div>
          );
        },
      },
      CareTeam: {
        accessorKey: "assignedTo",
        label: t("Care Team"),
        cell: ({ row }) => {
          const assignedTo = row.original.assignedTo || [];
          return (
            <div className="flex flex-nowrap gap-1 overflow-x-auto scrollbar-none mr-2">
              {assignedTo.length > 0 ? (
                assignedTo.slice(0, 3).map((uid) => {
                  const user = userRecordsById[uid];
                  const displayName = user
                    ? `${user.fname || ""} ${user.lname || ""}`.trim() || user.email || "Unknown User"
                    : `${uid.substring(0, 8)}...`;
                  return (
                    <Badge key={uid} variant="secondary" className="text-xs">
                      {displayName}
                    </Badge>
                  );
                })
              ) : (
                <span className="text-muted-foreground text-sm">{t("Unassigned")}</span>
              )}
              {assignedTo.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  {t("+ {{amount}} more", { amount: assignedTo.length - 3 })}
                </Badge>
              )}
            </div>
          );
        },
      },
      Tags: {
        accessorKey: "tags",
        label: t("Tags"),
        cell: ({ row }) => {
          const tags = row.original.tags || [];
          return (
            <div className="flex flex-nowrap gap-1 overflow-x-auto scrollbar-none mr-2">
              {tags.length > 0 ? (
                tags.slice(0, 3).map((tag, idx) => (
                  <Badge key={tag + String(idx)} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))
              ) : (
                <span className="text-muted-foreground text-sm">{t("No tags")}</span>
              )}
              {tags.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  {t("+ {{amount}} more", { amount: tags.length - 3 })}
                </Badge>
              )}
            </div>
          );
        },
      },
      DateOfBirth: {
        accessorKey: "dateOfBirth",
        label: t("Date of Birth"),
        cell: ({ row }) => {
          const dob = row.original.dateOfBirth;
          return (
            <div className="text-sm">
              {dob ? formatDate(dob, "MMM D, YYYY", false) : <p className="text-muted-foreground">{t("Not set")}</p>}
            </div>
          );
        },
      },
    }),
    [onSelectEnduser, t, userRecordsById]
  );

  const enduserFieldConfigMap = useMemo(() => {
    const configMap: Record<string, PartialEnduserColumnDef> = {};
    const entries = Object.entries(ALL_ENDUSER_FIELDS_TO_DISPLAY_NAME);

    for (const [field, displayName] of entries) {
      if (ENDUSER_FIELD_KEYS_ALREADY_COVERED.has(field)) continue;
      configMap[field] = {
        accessorKey: field as keyof EnduserRecord,
        label: displayName,
        cell: ({ row }) => <EnduserFieldCell enduserId={row.original.id} fieldSelector={field} />,
      };
    }

    return configMap;
  }, []);

  const customFieldConfigMap = useMemo(() => {
    const customFields = organization?.settings?.endusers?.customFields;
    if (!customFields || !Array.isArray(customFields)) return {};

    const configMap: Record<string, PartialEnduserColumnDef> = {};
    customFields.forEach((customField) => {
      const fieldKey = `custom.${customField.field}`;
      configMap[fieldKey] = {
        accessorKey: "id",
        label: customField.field,
        cell: ({ row }) => (
          <EnduserFieldCell
            enduserId={row.original.id}
            fieldSelector={(enduser) => enduser.fields?.[customField.field]}
          />
        ),
      };
    });
    return configMap;
  }, [organization?.settings?.endusers?.customFields]);

  const combinedFieldToColumnConfigMap = useMemo(
    () => ({
      ...fieldToColumnConfigMap,
      ...enduserFieldConfigMap,
      ...customFieldConfigMap,
    }),
    [fieldToColumnConfigMap, enduserFieldConfigMap, customFieldConfigMap]
  );

  const {
    views,
    selectedView,
    columns,
    viewId,
    setViewId: setViewIdInternal,
    isLoadingViews,
    isDefaultView,
  } = useTableView<EnduserRecord>({
    page: "T2-Endusers",
    defaultView: defaultViewByUserId[userId] ?? DEFAULT_CONTACTS_VIEW,
    fieldToColumnConfigMap: combinedFieldToColumnConfigMap,
    viewId: providedViewId,
    setViewId,
  });

  const activeFilters = useMemo<ContactsFilters>(() => {
    const { sorting: _sorting, ...filters } = selectedView.filter ?? {};
    return filters;
  }, [selectedView.filter]);

  const viewConfig = useTableViewConfig<ContactsFilters>({
    isDefaultView,
    userId,
    selectedView,
    columns,
    allColumnConfigMap: combinedFieldToColumnConfigMap,
    defaultView: DEFAULT_CONTACTS_VIEW,
    setUserDefaultView,
    activeFilters,
    countActiveFilters: countContactActiveFilters,
  });

  // Build mdbFilter from active filters
  const queryMdbFilter = useMemo(() => {
    const mdbFilter: Record<string, unknown> = {};

    // Care team filter
    if (activeFilters.careTeamIds && activeFilters.careTeamIds.length > 0) {
      mdbFilter.assignedTo = { $in: activeFilters.careTeamIds };
    }

    // Tags filter
    if (activeFilters.tags && activeFilters.tags.tags.length > 0) {
      const { tags, behavior } = activeFilters.tags;
      if (behavior === "match_all") {
        mdbFilter.tags = { $all: tags };
      } else if (behavior === "match_any") {
        mdbFilter.tags = { $in: tags };
      } else {
        mdbFilter.tags = { $nin: tags };
      }
    }

    // Date of birth filter
    if (
      activeFilters.dateOfBirth &&
      (activeFilters.dateOfBirth.from !== undefined || activeFilters.dateOfBirth.to !== undefined)
    ) {
      const dobFilter: Record<string, number> = {};
      if (activeFilters.dateOfBirth.from !== undefined) {
        dobFilter.$gte = activeFilters.dateOfBirth.from;
      }
      if (activeFilters.dateOfBirth.to !== undefined) {
        dobFilter.$lte = activeFilters.dateOfBirth.to;
      }
      mdbFilter.dateOfBirth = dobFilter;
    }

    // Source filter
    if (activeFilters.source && activeFilters.source.sources.length > 0) {
      const { sources, behavior } = activeFilters.source;
      if (behavior === "match_none") {
        mdbFilter.source = { $nin: sources };
      } else {
        mdbFilter.source = { $in: sources };
      }
    }

    // Freeform filters
    if (activeFilters.freeform && activeFilters.freeform.length > 0) {
      const freeformMdbFilter = buildMdbFilterFromFreeform(activeFilters.freeform);
      Object.assign(mdbFilter, freeformMdbFilter);
    }

    return Object.keys(mdbFilter).length > 0 ? mdbFilter : undefined;
  }, [
    activeFilters.careTeamIds,
    activeFilters.tags,
    activeFilters.dateOfBirth,
    activeFilters.source,
    activeFilters.freeform,
  ]);

  const endusersQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {
      search: debouncedQuery ? { query: debouncedQuery } : undefined,
      mdbFilter: queryMdbFilter,
      limit: debouncedQuery ? undefined : 500,
    },
    select: (data) => data.pages.flat(),
  });

  const createTableViewMutation = useTellescopeMutation({
    model: "table_views",
    method: "createOne",
  });

  const deleteTableViewMutation = useTellescopeMutation({
    model: "table_views",
    method: "deleteOne",
  });

  const virtualizedTableProps = {
    data: endusersQuery.data ?? [],
    columns,
    getRowId: (row: EnduserRecord) => row.id,
    enableInternalFiltering: false,
    globalFilter: searchQuery,
    onGlobalFilterChange: setSearchQuery,
    sorting: viewConfig.sorting,
    setSorting: viewConfig.onSortingChange,
    onLoadMore: () => {
      endusersQuery.fetchNextPage();
    },
    hasMoreData: endusersQuery.hasNextPage,
    isEmpty: endusersQuery.isFetched && !endusersQuery.data?.length,
    isLoadingMore: endusersQuery.isLoading,
    onRowClick: (row) => onSelectEnduser(row.id),
    hoveredRowId,
    onRowHover: setHoveredRowId,
  } satisfies Partial<VirtualizedDataTableProps<EnduserRecord>>;

  return {
    endusersQuery,
    virtualizedTableProps,
    views,
    selectedView,
    viewId,
    setViewId: setViewIdInternal,
    isLoadingViews,
    createTableViewMutation,
    deleteTableViewMutation,
    TELLESCOPE_2_TABLE_VIEW_PREFIX,
    ...viewConfig,
  };
};
